/* Lazy, visible-only video loops; posters and all mode text remain available without JS. */
(() => {
  if (customElements.get('ac-pro-experience')) return;

  class AcProExperience extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.videos = [...this.querySelectorAll('video')];
      this.loopVideos = this.videos.filter(video => video.hasAttribute('data-pro-loop'));
      this.manualVideos = this.videos.filter(video => !video.hasAttribute('data-pro-loop'));
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.tabs = [...this.querySelectorAll('[data-pro-tab]')];
      this.panels = [...this.querySelectorAll('[data-pro-panel]')];
      this.tabList = this.querySelector('[data-pro-tabs]');

      if (this.tabs.length && this.tabs.length === this.panels.length) {
        this.tabList.setAttribute('role', 'tablist');
        this.tabs.forEach((tab, index) => {
          tab.setAttribute('role', 'tab');
          this.panels[index].setAttribute('role', 'tabpanel');
          this.panels[index].setAttribute('aria-labelledby', tab.id);
          this.panels[index].tabIndex = 0;
          tab.addEventListener('click', () => this.selectMode(index), { signal });
          tab.addEventListener('keydown', event => this.onTabKeydown(event, index), { signal });
        });
        this.tabList.hidden = false;
        this.selectMode(0);
        this.dataset.enhanced = '';
      }

      this.manualVideos.forEach(video => {
        const media = video.closest('[data-pro-media]');
        if (!media) return;
        const button = media.querySelector('[data-pro-play]');
        const error = media.querySelector('[data-pro-media-error]');
        if (!button || !error) return;
        button.hidden = false;
        button.addEventListener('click', async () => {
          error.hidden = true;
          try {
            await video.play();
            if (!this.isConnected || signal.aborted) return;
            video.focus({ preventScroll: true });
          } catch (cause) {
            if (!this.isConnected || signal.aborted) return;
            if (cause?.name !== 'AbortError') {
              error.hidden = false;
              button.hidden = true;
              error.querySelector('a')?.focus({ preventScroll: true });
            }
          }
        }, { signal });
        video.addEventListener('play', () => {
          button.hidden = true;
          error.hidden = true;
          this.manualVideos.forEach(other => { if (other !== video) other.pause(); });
        }, { signal });
        const showPlayButton = () => { if (error.hidden) button.hidden = false; };
        video.addEventListener('pause', showPlayButton, { signal });
        video.addEventListener('ended', showPlayButton, { signal });
        const showError = () => { error.hidden = false; button.hidden = true; };
        video.addEventListener('error', showError, { signal });
        video.querySelector('source')?.addEventListener('error', showError, { signal });
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
        }, { threshold: 0 });
        this.manualVideos.forEach(video => this.observer.observe(video));
      }
      this.setupLoopMedia(signal);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseVideos();
        else this.loopVideos.forEach(video => this.syncLoop(video));
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.selectMode(index);
      }, { signal });
    }

    selectMode(index, moveFocus = false) {
      this.manualVideos.forEach(video => video.pause());
      // Pause immediately, before observers report the newly hidden panel.
      this.panels.forEach(panel => panel.querySelectorAll('[data-pro-loop]').forEach(video => {
        video.dataset.inView = 'false';
        video.autoplay = false;
        video.pause();
      }));
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[tabIndex].hidden = !selected;
        // Request fresh visibility even when the user switches tabs rapidly.
        this.panels[tabIndex].querySelectorAll('[data-pro-loop]').forEach(video => {
          [this.loopLoadObserver, this.loopPlayObserver].forEach(observer => {
            observer?.unobserve(video);
            observer?.observe(video);
          });
        });
      });
      if (moveFocus) this.tabs[index].focus({ preventScroll: true });
    }

    onTabKeydown(event, index) {
      let next;
      const last = this.tabs.length - 1;
      const rtl = getComputedStyle(this.tabList).direction === 'rtl';
      if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = last;
      if (next === undefined) return;
      event.preventDefault();
      this.selectMode(next, true);
    }

    pauseVideos() {
      this.videos?.forEach(video => video.pause());
    }

    loadLoop(video) {
      if (video.dataset.loaded || this.reducedMotion.matches || video.closest('[data-pro-panel]')?.hidden) return;
      if (!video.dataset.src) return;
      video.dataset.loaded = 'true';
      video.src = video.dataset.src;
      delete video.dataset.src;
      video.load();
    }

    syncLoop(video) {
      const shouldPlay = this.isConnected && !document.hidden && !this.reducedMotion.matches && !video.closest('[data-pro-panel]')?.hidden && video.dataset.inView === 'true' && !video.dataset.failed;
      video.autoplay = shouldPlay;
      if (!shouldPlay) {
        video.pause();
        return;
      }
      this.loadLoop(video);
      video.play().then(() => {
        if (!this.isConnected || document.hidden || this.reducedMotion.matches || video.closest('[data-pro-panel]')?.hidden || video.dataset.inView !== 'true') video.pause();
      }).catch(() => {
        // A browser may block autoplay (for example in Low Power Mode). Keep the poster.
      });
    }

    setupLoopMedia(signal) {
      this.loopVideos.forEach(video => {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = false;
        const showError = () => {
          if (!video.dataset.loaded) return;
          video.dataset.failed = 'true';
          const error = video.closest('[data-pro-media]')?.querySelector('[data-pro-media-error]');
          if (error) error.hidden = false;
        };
        video.addEventListener('error', showError, { signal });
      });
      if ('IntersectionObserver' in window) {
        this.loopLoadObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting) this.loadLoop(entry.target); });
        }, { rootMargin: '240px 0px' });
        this.loopPlayObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            entry.target.dataset.inView = String(entry.isIntersecting && entry.intersectionRatio >= .15);
            this.syncLoop(entry.target);
          });
        }, { threshold: [0, .15] });
        this.loopVideos.forEach(video => {
          this.loopLoadObserver.observe(video);
          this.loopPlayObserver.observe(video);
        });
      }
      this.reducedMotion.addEventListener('change', () => {
        this.loopVideos.forEach(video => this.syncLoop(video));
      }, { signal });
    }

    disconnectedCallback() {
      this.pauseVideos();
      this.observer?.disconnect();
      this.loopLoadObserver?.disconnect();
      this.loopPlayObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  }

  customElements.define('ac-pro-experience', AcProExperience);
})();
